
/// <amd-module name="VSSUI/Components/FileInput/FileInput" />
import * as React from "react";
import { format } from "VSS/Core/Util/String";
import { Button } from "VSSUI/Button";
import { Link } from "VSSUI/Link";
import * as VSSUIResources from "VSSUI/Resources.FileInput";
import { css } from "VSSUI/Util";
import {
    FileEncoding,
    FileInputContentType,
    FileInputControlLimitEventData,
    FileInputData,
    FileInputResult,
    IFileInputProps
} from "../../Components/FileInput/FileInput.Props";

export interface FileInputState {
    files?: FileInputData[];
    errorMessage?: string;
}

/*
 * Make a best-effort attempt to determine the file encoding for the given base64 encoded file contents
 *
 * @param base64Content Base64 encoded string of the file contents
 */
function tryDetectFileEncoding(base64Content: string) {
    if (typeof window.atob === "undefined") {
        return FileEncoding.Unknown;
    }

    const decodedContent = window.atob(base64Content);

    // Read the byte order mark
    const bom: number[] = [];
    for (let i = 0; i < 4; i++) {
        bom.push(decodedContent.charCodeAt(i));
    }

    if (bom[0] === 0xfe && bom[1] === 0xff) {
        return FileEncoding.UTF16_BE;
    } else if (bom[0] === 0xff && bom[1] === 0xfe) {
        if (bom[2] === 0x00 && bom[3] === 0x00) {
            return FileEncoding.UTF32_LE;
        } else {
            return FileEncoding.UTF16_LE;
        }
    } else if (bom[0] === 0xef && bom[1] === 0xbb && bom[2] === 0xbf) {
        return FileEncoding.UTF8;
    } else if (bom[0] === 0x00 && bom[1] === 0x00 && bom[2] === 0xfe && bom[3] === 0xff) {
        return FileEncoding.UTF32_BE;
    }

    let isAscii = true;

    // Check for binary files
    for (let i = 0, l = decodedContent.length; i < l; i++) {
        const charCode = decodedContent.charCodeAt(i);

        // If buffer[i] is 0-0x1F OR 0x7F but not (TAB, CR, LF, FF, or ^Z)
        // then the file is a binary file.
        if (
            (charCode <= 0x1f || charCode == 0x7f) &&
            (charCode != 0x9 && charCode != 0xc && charCode != 0xd && charCode != 0xa && charCode != 0x1a)
        ) {
            return FileEncoding.Binary;
        } else if (charCode > 0x7f) {
            isAscii = false;
        }
    }

    if (isAscii) {
        return FileEncoding.ASCII;
    }

    // Treat non-binary files without an explicit BOM as UTF8
    return FileEncoding.UTF8;
}

export class FileInput extends React.Component<IFileInputProps, FileInputState> {
    private _files: FileInputData[] = [];

    public componentDidMount() {
        if (this.props.initialFiles) {
            this._addFiles(this.props.initialFiles);
        }
    }

    public render(): JSX.Element {
        const maximumNumberOfFiles: number = this.props.maximumNumberOfFiles || 0;
        const files = this._files;
        const errorMessage = this.state && this.state.errorMessage ? this.state.errorMessage : "";
        const canAddMoreFiles = !maximumNumberOfFiles || files.length < maximumNumberOfFiles;

        let accept: string | null = null;
        if (this.props.allowedFileExtensions) {
            const fileExtensions: string[] = this.props.allowedFileExtensions.map(extension => {
                if (extension[0] !== ".") {
                    return "." + extension;
                } else {
                    return extension;
                }
            });
            accept = fileExtensions.join();
        }

        const dragDropLabel = maximumNumberOfFiles === 1 ? VSSUIResources.FileInputDragDropSingleFileLabel : VSSUIResources.FileInputDragDropLabel;
        const multiple = maximumNumberOfFiles !== 1;
        return (
            <FileInputPure
                dragDropLabel={dragDropLabel}
                multiple={multiple}
                canAddMoreFiles={canAddMoreFiles}
                accept={accept}
                files={files}
                errorMessage={errorMessage}
                addFiles={this._addFiles}
                remove={this._remove}
                clear={this._clear}
            />
        );
    }

    private _clear = (): void => {
        this._files.forEach(pendingResult => {
            if (pendingResult.fileReader.readyState !== (window.FileReader as any).DONE) {
                pendingResult.fileReader.abort();
            }
        });
        this._files = [];
        this.setState({
            files: this._files,
            errorMessage: ""
        });
        this._triggerUpdateEvent();
    };

    private _remove = (file: FileInputData): void => {
        this._files = this._files.filter(r => r !== file);
        this.setState({
            files: this._files
        });
        this._triggerUpdateEvent();
    };

    private _addFiles = (files: FileList): void => {
        // Make sure the file type selected is of the allowed file extenstion type
        if (this.props.allowedFileExtensions && this.props.allowedFileExtensions.length > 0) {
            for (let i = 0; i < files.length; i++) {
                const fileExtension: string | undefined = files[i].name.split(".").pop();
                let isAllowed = false;
                for (let j = 0; j < this.props.allowedFileExtensions.length; j++) {
                    if (fileExtension!.toLocaleLowerCase() === this.props.allowedFileExtensions[j].toLocaleLowerCase()) {
                        isAllowed = true;
                        break;
                    }
                }
                if (!isAllowed) {
                    const errorMessage = format(
                        VSSUIResources.FileInputErrorNotAllowedExtensionType,
                        fileExtension,
                        this.props.allowedFileExtensions.join()
                    );
                    this.setState({
                        errorMessage: errorMessage
                    });
                    return;
                }
            }
        }

        // Make sure the file names are unique, so build a hash set of names
        const fileNames: { [name: string]: number } = {};
        for (let i = 0, l = this._files.length; i < l; i++) {
            fileNames[this.props.fileNamesCaseSensitive ? this._files[i].result.name : this._files[i].result.name.toLowerCase()] = 1;
        }

        // Loop through each file checking size and name uniqueness
        let totalSize = this._getTotalFilesSize();
        for (let i = 0, l = files.length; i < l; i++) {
            if (this.props.maximumSingleFileSize && files[i].size > this.props.maximumSingleFileSize) {
                const errorMessage = this._getLimitErrorMessage(
                    format(
                        VSSUIResources.FileInputErrorMaxFileSize,
                        files[i].name,
                        this._getFriendlySizeString(files[i].size),
                        this._getFriendlySizeString(this.props.maximumSingleFileSize)
                    ),
                    { fileSize: files[i].size }
                );
                this.setState({
                    errorMessage: errorMessage
                });
                return;
            }
            totalSize += files[i].size;

            const fileNameKey = this.props.fileNamesCaseSensitive ? files[i].name : files[i].name.toLowerCase();
            if (fileNames[fileNameKey]) {
                const errorMessage = format(VSSUIResources.FileInputErrorDuplicateFileName, files[i].name);
                this.setState({
                    errorMessage: errorMessage
                });
                return;
            }
            fileNames[fileNameKey] = 1;
        }

        // Check overall file size
        if (this.props.maximumTotalFileSize && totalSize > this.props.maximumTotalFileSize) {
            const errorMessage = this._getLimitErrorMessage(
                format(
                    VSSUIResources.FileInputErrorTotalFileSize,
                    this._getFriendlySizeString(totalSize),
                    this._getFriendlySizeString(this.props.maximumTotalFileSize)
                ),
                { totalSize }
            );
            this.setState({
                errorMessage: errorMessage
            });
            return;
        }

        // Check total number of files
        const fileCount = this._files.length + files.length;
        if (this.props.maximumNumberOfFiles && fileCount > this.props.maximumNumberOfFiles) {
            const message =
                this.props.maximumNumberOfFiles === 1
                    ? VSSUIResources.FileInputErrorSingleFileOnly
                    : format(VSSUIResources.FileInputErrorMaxNumFiles, this.props.maximumNumberOfFiles);

            const errorMessage = this._getLimitErrorMessage(message, { fileCount });
            this.setState({
                errorMessage: errorMessage
            });
            return;
        }

        //
        // Add the files (error checks have passed)
        //
        for (let i = 0, l = files.length; i < l; i++) {
            this._addFile(files[i]);
        }
    };

    private _addFile(file: File): void {
        //
        // Setup the pending result
        //
        const result: FileInputResult = {
            name: file.name,
            size: file.size,
            type: file.type,
            lastModifiedDate: file.lastModified ? new Date(file.lastModified) : (file as any).lastModifiedDate,
            statusText: VSSUIResources.Loading,
            isError: false
        };

        const fileReader = new window.FileReader();

        const fileInputData: FileInputData = {
            result: result,
            fileReader: fileReader
        };

        this._files.push(fileInputData);

        // Helper functions
        const _triggerUpdate = () => {
            this.setState({
                files: this._files,
                errorMessage: this.state && this.state.errorMessage ? this.state.errorMessage : ""
            });
            this._triggerUpdateEvent();
        };
        const _addResult = () => {
            result.statusText = this._getFriendlySizeString(file.size);
            _triggerUpdate();
        };

        //
        // Setup the read operation
        //

        fileReader.onload = (e: ProgressEvent) => {
            result.encoding = FileEncoding.Unknown;

            if (this.props.resultContentType === FileInputContentType.RawText) {
                result.content = (fileReader.result as string) || "";
            } else {
                const dataUrl: string = fileReader.result as string;

                // If the file is empty, Chrome returns result="data:" and IE returns result=null, so we check for both of these cases.
                if (dataUrl && dataUrl.indexOf(",") > -1) {
                    // Get the base64 content portion of the data url
                    result.content = dataUrl.substr(dataUrl.indexOf(",") + 1);
                    if (result.content.substr(0, 2) === "//" && result.content.length % 4 === 2) {
                        // Trim a leading "//" only if it isn't part of the base64 content itself (base64 length is a multiple of 4).
                        result.content = result.content.substr(2);
                    }
                } else {
                    result.content = "";
                }

                if (this.props.detectEncoding) {
                    result.encoding = tryDetectFileEncoding(result.content);
                }
            }

            _addResult();
        };
        fileReader.onerror = (e: ProgressEvent) => {
            const errorMessage = format(VSSUIResources.FileInputReadErrorFormat, result.name, fileReader.error!.message);
            result.statusText = VSSUIResources.FileInputErrorLabel;
            result.isError = true;
            _triggerUpdate();
        };
        fileReader.onloadend = (e: any) => {
            _triggerUpdate();
        };

        //
        // Start the read operation
        //
        if (this.props.resultContentType === FileInputContentType.RawText) {
            fileReader.readAsText(file);
        } else if (this.props.resultContentType === FileInputContentType.RawFile) {
            result.encoding = FileEncoding.Unknown;
            result.file = file;

            _addResult();
            _triggerUpdate();
        } else {
            fileReader.readAsDataURL(file);
        }
    }

    private _triggerUpdateEvent(): void {
        if (this.props.updateHandler) {
            this.props.updateHandler({
                loading: this._files.some(file => {
                    return file.fileReader.readyState !== (window.FileReader as any).DONE;
                }),
                files: this._files
            });
        }
    }

    private _getTotalFilesSize(): number {
        let totalSize = 0;
        const files = this._files;
        for (let i = 0, l = files.length; i < l; i++) {
            totalSize += files[i].result.size;
        }
        return totalSize;
    }

    private _getLimitErrorMessage(errorText: string, limitData: FileInputControlLimitEventData) {
        let message = errorText;

        if (this.props.limitMessageFormatter) {
            message = this.props.limitMessageFormatter(errorText, limitData);
        }

        return message;
    }

    /**
     * Get a friendly string representing the length of a file (e.g. "61.2 KB")
     *
     * @param numBytes Number of bytes
     * @param decimalPlaces Number of decimal places to round to. (e.g. 0 -> "61 KB", 1 -> "61.2 KB", 2 -> "61.18 KB")
     */
    private _getFriendlySizeString(numBytes: number, decimalPlaces: number = 1) {
        const divider = Math.pow(10, decimalPlaces);
        if (numBytes < 1024) {
            return format(VSSUIResources.FileSizeBytesFormat, numBytes);
        } else if (numBytes < 1024 * 1024) {
            return format(VSSUIResources.FileSizeKBFormat, Math.round((numBytes / 1024) * 10) / 10);
        } else {
            return format(VSSUIResources.FileSizeMBFormat, Math.round((numBytes / (1024 * 1024)) * 10) / 10);
        }
    }
}

interface FileInputPureProps {
    dragOverCssClass?: string;
    dragDropLabel: string;
    multiple: boolean;
    accept: string | null;
    errorMessage: string;
    files: FileInputData[];
    canAddMoreFiles: boolean;
    addFiles: (files: FileList) => void;
    remove: (file: FileInputData) => void;
    clear: () => void;
}

interface FileInputPureState {
    isDragging?: boolean;
}

class FileInputPure extends React.Component<FileInputPureProps, FileInputPureState> {
    private _fileInput: HTMLInputElement;

    public render(): JSX.Element {
        // the file input is not shown. instead, we show a pretty drag-drop container with a browse button.
        const fileInputStyle = {
            display: "none"
        };

        const fileListStyle = {
            display: "block"
        };

        const dropTargetClassName = css(
            "vss-FileInput-filesDropTarget",
            this.state.isDragging ? this.props.dragOverCssClass || "vss-FileInput-dragOver" : ""
        );

        return (
            <div className="vss-FileInput">
                {this.props.canAddMoreFiles && (
                    <div
                        className={dropTargetClassName}
                        onDragOver={this._onDragOver}
                        onDragLeave={this._onDragLeave}
                        onDragEnter={this._onDragEnter}
                        onDrop={this._onDrop}
                    >
                        <div className="vss-FileInput-dragDropTable flex-row" role="presentation">
                            <div className="vss-FileInput-dragDropLabel flex-grow">
                                <span>{this.props.dragDropLabel}</span>
                            </div>
                            <div className="vss-FileInput-browseButton">
                                <Button className="vss-FileInput-browseContainer" onClick={this._onBrowseContainerClick}>
                                    {VSSUIResources.FileInputBrowseButtonLabel}
                                </Button>
                                <input
                                    type="file"
                                    ref={this._resolveFileInput}
                                    onChange={this._onFileInputChange}
                                    accept={this.props.accept!}
                                    multiple={this.props.multiple}
                                    style={fileInputStyle}
                                />
                            </div>
                        </div>
                    </div>
                )}
                {this.props.files.length > 1 && (
                    <div className="vss-FileInput-overallStatusContainer">
                        <span className="vss-FileInput-overallStatusActions">
                            <Link onClick={this._onClearClick}>{VSSUIResources.FileInputRemoveAll}</Link>
                        </span>
                    </div>
                )}
                <div className="vss-FileInput-errorContainer">{this.props.errorMessage}</div>
                {this.props.files.length > 0 && (
                    <ul className="vss-FileInput-fileList" style={fileListStyle}>
                        {this.props.files.map((file: FileInputData) => {
                            return (
                                <li key={file.result.name}>
                                    <div className="vss-FileInput-headerContainer">
                                        <span className="vss-FileInput-fileName">{file.result.name}</span>
                                    </div>
                                    <div className="vss-FileInput-detailsContainer">
                                        <span className={file.result.isError ? "vss-FileInput-status-error" : "vss-FileInput-status"}>
                                            {file.result.statusText}
                                        </span>
                                        <span className="vss-FileInput-links">
                                            <Button tabIndex={0} onClick={() => this._onRemoveClick(file)} role="button">
                                                {VSSUIResources.FileInputRemoveFile}
                                            </Button>
                                        </span>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        );
    }

    public componentWillMount(): void {
        this.setState({
            isDragging: false
        });
    }

    private _onClearClick = (): void => {
        if (this.props.clear) {
            this.props.clear();
        }
    };

    private _onRemoveClick = (file: FileInputData): void => {
        if (this.props.remove) {
            this.props.remove(file);
        }
    };

    private _onBrowseContainerClick = (): void => {
        if (this._fileInput) {
            this._fileInput.click();
        }
    };

    private _resolveFileInput = (element: HTMLInputElement): void => {
        this._fileInput = element;
    };

    private _onDragEnter = (e: React.DragEvent<HTMLDivElement>): void => {
        this._setDropEffect(e);
        e.preventDefault();
        this.setState({
            isDragging: true
        });
    };

    private _onDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
        e.preventDefault();
        this.setState({
            isDragging: false
        });
    };

    private _onDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
        this._setDropEffect(e);
        e.preventDefault();
        this.setState({
            isDragging: true
        });
    };

    private _onDrop = (e: React.DragEvent<HTMLDivElement>): void => {
        this._setDropEffect(e);
        e.preventDefault();
        this.setState({
            isDragging: false
        });

        const fileList = e.dataTransfer.files;
        if (fileList && fileList.length > 0) {
            if (this.props.addFiles) {
                this.props.addFiles(fileList);
            }
        }
    };

    private _onFileInputChange = (args: React.FormEvent<HTMLInputElement>): void => {
        if (args.currentTarget.files && args.currentTarget.files.length) {
            if (this.props.addFiles) {
                this.props.addFiles(args.currentTarget.files);
            }
            args.currentTarget.value = "";
        }
    };

    private _setDropEffect(e: React.DragEvent<HTMLDivElement>): void {
        /*
         * React wraps event handlers inside other events in the development build to get better devtool integration.
         * Problem is that Edge and IE don't allow access to the dropEffect property unless you're inside that original event handler.
         * In the production build there are no wrapper events, it's just a function call, so dropEffect can be accessed normally.
         *
         * See https://github.com/facebook/react/issues/5700
         */
        try {
            e.dataTransfer.dropEffect = "copy";
        } catch (ex) {
            /*
             * So, we just ignore the exception here.
             */
        }
    }
}
